import { useCallback, useEffect, useState } from "react";

import { humanizeApiError } from "../api/client";
import {
  type BitrixHealth,
  type BitrixNotification,
  type BitrixNotificationListResponse,
  type BitrixNotificationStatus,
  bitrixApi,
} from "../api/bitrix";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyState } from "../components/ui/empty-state";
import { Select } from "../components/ui/select";
import { TableSkeleton } from "../components/ui/table-skeleton";

const HEALTH_REFRESH_MS = 30_000;
const RETRY_TOAST_MS = 4_000;

const STATUS_LABEL: Record<BitrixNotificationStatus, string> = {
  queued: "В очереди",
  sending: "Отправляется",
  sent: "Отправлено",
  failed: "Ошибка",
  dlq: "Не отправлено",
};

const STATUS_BADGE_CLASS: Record<BitrixNotificationStatus, string> = {
  queued: "bg-amber-50 text-amber-700",
  sending: "bg-blue-50 text-blue-700",
  sent: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  dlq: "bg-red-100 text-red-800",
};

const STATUS_FILTERS: { label: string; value: BitrixNotificationStatus | "" }[] = [
  { label: "Все", value: "" },
  { label: "В очереди", value: "queued" },
  { label: "Отправляется", value: "sending" },
  { label: "Не отправлено", value: "dlq" },
  { label: "Отправлено", value: "sent" },
  { label: "Ошибка", value: "failed" },
];

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "medium",
  });
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err);
}

export function BitrixHealthPage() {
  const [health, setHealth] = useState<BitrixHealth | null>(null);
  const [list, setList] = useState<BitrixNotificationListResponse | null>(null);
  const [statusFilter, setStatusFilter] = useState<BitrixNotificationStatus | "">("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retrySuccess, setRetrySuccess] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, l] = await Promise.all([
        bitrixApi.health(),
        bitrixApi.listNotifications({
          size: 50,
          ...(statusFilter ? { status: statusFilter } : {}),
        }),
      ]);
      setHealth(h);
      setList(l);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Авто-обновление health (не списка): для дашборда нужны свежие
  // счётчики, но журнал перезагружать каждые 30 сек — лишний шум.
  useEffect(() => {
    const id = window.setInterval(() => {
      void bitrixApi
        .health()
        .then(setHealth)
        .catch(() => {
          /* фоновый рефреш — ошибки прячем, основная загрузка их и так покажет */
        });
    }, HEALTH_REFRESH_MS);
    return () => window.clearInterval(id);
  }, []);

  // Success-плашку прячем через несколько секунд, чтобы не торчала навсегда.
  useEffect(() => {
    if (retrySuccess === null) return;
    const id = window.setTimeout(() => setRetrySuccess(null), RETRY_TOAST_MS);
    return () => window.clearTimeout(id);
  }, [retrySuccess]);

  async function onRetry(id: string) {
    setRetryingId(id);
    setError(null);
    try {
      await bitrixApi.retry(id);
      setRetrySuccess("Поставлено в очередь повторно");
      // После успешного retry — перезагружаем оба представления.
      await loadAll();
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setRetryingId(null);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Уведомления в Bitrix24</h1>
      <p className="text-sm text-muted-foreground">
        Менеджеры получают сообщения в Bitrix24, когда клиент открывает КП
        («горячий лид») или когда срок ссылки скоро истекает. На этой странице
        видно, что доходит, что застряло и почему.
      </p>

      {error && (
        <Card>
          <CardContent className="py-3 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      {retrySuccess && (
        <div
          role="status"
          className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
          data-testid="bitrix-retry-success"
        >
          {retrySuccess}
        </div>
      )}

      {health && !health.webhook_configured && <WebhookNotConfiguredBanner />}

      <HealthSummaryCard
        health={health}
        loading={loading && !health}
        hasError={error !== null && health === null}
      />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-medium">
            Журнал уведомлений
            {list && (
              <span
                className="ml-2 text-sm font-normal text-muted-foreground"
                data-testid="bitrix-journal-total"
              >
                найдено: {list.total_count}
              </span>
            )}
          </CardTitle>
          <div className="flex items-center gap-2 text-sm">
            <label
              htmlFor="bitrix-status-filter"
              className="text-muted-foreground"
            >
              Статус:
            </label>
            <Select
              id="bitrix-status-filter"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as BitrixNotificationStatus | "")
              }
              className="w-auto"
              data-testid="bitrix-status-filter"
            >
              {STATUS_FILTERS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {list === null && loading ? (
            <NotificationsTable
              items={[]}
              retryingId={retryingId}
              onRetry={onRetry}
              loading
            />
          ) : list === null ? (
            // Запрос упал и в стейте ничего нет — скелетон уже не имеет смысла,
            // сама ошибка показана в баннере выше. Здесь даём явный fallback,
            // чтобы блок не оставался пустым/со «скелетонами навсегда».
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Не удалось загрузить журнал. Попробуйте обновить страницу.
            </div>
          ) : list.items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              Уведомлений ещё нет
            </div>
          ) : (
            <NotificationsTable
              items={list.items}
              retryingId={retryingId}
              onRetry={onRetry}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WebhookNotConfiguredBanner() {
  return (
    <div
      role="alert"
      className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      data-testid="bitrix-webhook-banner"
    >
      <div className="font-medium">
        Bitrix24 не получает уведомления: webhook не настроен.
      </div>
      <div className="mt-1 text-amber-800">
        Воркер запущен, но шлёт «в никуда»: в <code>.env</code> не задан{" "}
        <code>BITRIX_WEBHOOK_URL</code>. Задайте его и перезапустите{" "}
        <code>tracelink-bitrix-sender</code>, иначе менеджеры не увидят
        ни «горячих лидов», ни напоминаний об истечении.
      </div>
    </div>
  );
}

function HealthSummaryCard({
  health,
  loading,
  hasError,
}: {
  health: BitrixHealth | null;
  loading: boolean;
  hasError?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-medium">Сводка</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground">Загрузка…</div>
        ) : health === null && hasError ? (
          <div className="text-sm text-muted-foreground">
            Сводку не удалось загрузить — детали в баннере выше.
          </div>
        ) : health === null ? (
          <EmptyState
            title="Здесь пока ничего нет"
            hint="Статистика по очереди появится после первой отправки в Bitrix24."
          />
        ) : (
          <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-3 text-sm">
            <Stat
              label="В очереди"
              value={health.queued}
              hint="ждут отправки"
            />
            <Stat
              label="Отправляется"
              value={health.sending}
              hint="воркер шлёт прямо сейчас"
            />
            <Stat
              label="Не отправлено"
              value={health.dlq}
              hint="исчерпаны 5 попыток"
              tone={health.dlq > 0 ? "danger" : undefined}
            />
            <Stat
              label="Ошибка"
              value={health.failed}
              hint="сбой, будет повтор"
            />
            <Stat
              label="За 24 ч"
              value={health.sent_recent_24h}
              hint="отправлено успешно"
            />
            {health.last_dlq_at && (
              <div className="col-span-2 sm:col-span-5 text-xs text-muted-foreground">
                Последняя «не отправлено»: {formatDate(health.last_dlq_at)}
              </div>
            )}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone?: "danger";
}) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd
        className={
          tone === "danger" && value > 0
            ? "text-2xl font-semibold text-red-700"
            : "text-2xl font-semibold"
        }
      >
        {value}
      </dd>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function NotificationsTable({
  items,
  retryingId,
  onRetry,
  loading,
}: {
  items: BitrixNotification[];
  retryingId: string | null;
  onRetry: (id: string) => void;
  loading?: boolean;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="text-muted-foreground">
        <tr className="border-b">
          <th className="text-left font-normal px-4 py-2">Создано</th>
          <th className="text-left font-normal px-4 py-2">Статус</th>
          <th className="text-left font-normal px-4 py-2">Тип</th>
          <th className="text-left font-normal px-4 py-2">Отправка</th>
          <th className="text-left font-normal px-4 py-2">Попытки</th>
          <th className="text-left font-normal px-4 py-2">Ошибка</th>
          <th className="text-left font-normal px-4 py-2">Действия</th>
        </tr>
      </thead>
      <tbody>
        {loading && <TableSkeleton columns={7} />}
        {items.map((n) => {
          const retryable = n.status === "dlq" || n.status === "failed";
          return (
            <tr key={n.id} className="border-b last:border-b-0 align-top">
              <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                {formatDate(n.created_at)}
                {n.message_preview && (
                  <div
                    className="mt-1 max-w-[28rem] truncate text-xs text-muted-foreground"
                    title={n.message_preview}
                    data-testid={`bitrix-row-message-${n.id}`}
                  >
                    {n.message_preview}
                  </div>
                )}
              </td>
              <td className="px-4 py-2">
                <span
                  className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_BADGE_CLASS[n.status]}`}
                  data-testid={`bitrix-row-status-${n.id}`}
                >
                  {STATUS_LABEL[n.status]}
                </span>
              </td>
              <td className="px-4 py-2 text-muted-foreground">
                {n.notification_kind === "hot_lead"
                  ? "Горячий лид"
                  : "Скоро истекает"}
              </td>
              <td className="px-4 py-2 font-mono">
                <a
                  href={`/dispatches/${n.dispatch_id}`}
                  className="text-blue-700 hover:underline"
                  title={n.dispatch_id}
                >
                  {n.dispatch_id.slice(0, 8)}…
                </a>
              </td>
              <td className="px-4 py-2">{n.attempt_count}</td>
              <td
                className="px-4 py-2 text-xs text-muted-foreground max-w-[24rem] truncate"
                title={n.last_error ?? undefined}
                data-testid={`bitrix-row-error-${n.id}`}
              >
                {n.last_error ?? "—"}
              </td>
              <td className="px-4 py-2">
                {retryable ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={retryingId === n.id}
                    onClick={() => onRetry(n.id)}
                    data-testid={`bitrix-row-retry-${n.id}`}
                  >
                    Повторить
                  </Button>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
